import type { Newsletter } from 'interfaces/Account';
import { type Dispatch, type JSX, type SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import unsubscribeUser from 'lib/personalizationApi/unsubscribeUser';
import { NoticeContext } from 'providers/NoticeProvider';
import type { ModalButtonProps } from 'components/Modal';

export default function useNewslettersModal(
  email: string,
  setUserNewsletters: Dispatch<SetStateAction<Newsletter[]>>,
  setLoading: Dispatch<SetStateAction<string>>,
  pagePath: string,
) {
  const { setNotification } = useContext(NoticeContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTarget, setModalTarget] = useState<Newsletter[]>([]);
  const [modalType, setModalType] = useState<'subscribe' | 'unsubscribe' | 'unsubscribeFromAll' | 'subscribeToAll'>(
    'subscribe',
  );

  const [checked, setChecked] = useState(false);

  const onModalClose = () => {
    setChecked(false);
  };

  useEffect(() => {
    if (!isModalOpen) {
      onModalClose();
    }
  }, [isModalOpen]);

  const unSubscribeAll = (userEmail: string, formName: string[]) => {
    unsubscribeUser(userEmail, '', formName, pagePath)
      .then(() => {
        setLoading('none');

        setUserNewsletters((prevUserNews) =>
          prevUserNews.map((n) => {
            if (formName.filter((id) => id === n.formName).length > 0) {
              return { ...n, subscriptionStatus: 'recently-unsubscribed' };
            }
            return n;
          }),
        );
      })
      .catch(() => {
        setNotification?.('error', 'There was an error when trying to unsubscribe from all the newsletters.');
        setLoading('none');
      });
  };

  const modalSettings = useMemo(() => {
    const settings = {
      subscribe: {
        children: '',
        className: '',
        firstButton: {
          onClick: () => {
            setIsModalOpen(false);
          },
          title: 'CONTINUE BROWSING',
        },
        onClose: () => setIsModalOpen(false),
        title: 'Thank you for subscribing.',
      },
      subscribeToAll: {
        children: '',
        className: '',
        firstButton: {
          onClick: () => {
            setIsModalOpen(false);
          },
          title: 'CONTINUE BROWSING',
        },
        onClose: () => setIsModalOpen(false),
        title: 'Thank you for subscribing.',
      },
      unsubscribe: {
        children: '',
        className: '',
        firstButton: {
          $variant: 'secondary',
          onClick: () => {
            setLoading('updating');
            unsubscribeUser(email, '', [modalTarget[0].formName], pagePath)
              .then(() => {
                setLoading('none');
                setUserNewsletters((prevUserNews) =>
                  prevUserNews.map((n) => {
                    if (n.formName === modalTarget[0].formName) {
                      return { ...n, subscriptionStatus: 'recently-unsubscribed' };
                    }
                    return n;
                  }),
                );
              })
              .catch(() => {
                setNotification?.(
                  'error',
                  `There was an error when trying to unsubscribe from the ${modalTarget[0].title} newsletter.`,
                );
                setLoading('none');
              });
            setIsModalOpen(false);
          },
          title: 'UNSUBSCRIBE',
        },
        onClose: () => setIsModalOpen(false),
        secondButton: {
          $variant: 'primary',
          onClick: () => setIsModalOpen(false),
          title: 'CANCEL',
        },
        title: 'Are you sure you want to unsubscribe?',
      },
      unsubscribeFromAll: {
        children: '',
        className: '',
        firstButton: {
          $variant: 'secondary',
          onClick: () => {
            setLoading('updating');
            const subscribedNewsletters = modalTarget
              .filter((nl) => nl.subscriptionStatus === 'subscribed')
              .map((newsletter) => newsletter.formName);
            unSubscribeAll(email, subscribedNewsletters);
            setIsModalOpen(false);
          },
          title: 'UNSUBSCRIBE',
        },
        onClose: () => setIsModalOpen(false),
        secondButton: {
          $variant: 'primary',
          onClick: () => setIsModalOpen(false),
          title: 'CANCEL',
        },
        title: 'Are you sure you want to unsubscribe?',
      },
    };
    return settings[modalType] as {
      children: JSX.Element | string;
      className: string;
      firstButton?: ModalButtonProps;
      secondButton?: ModalButtonProps;
      onClose: () => void;
      title: string;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalTarget, modalType, checked, email]);

  return {
    isModalOpen,
    modalSettings,
    setIsModalOpen,
    setModalTarget,
    setModalType,
  };
}
